import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { FilterProvider } from "./contexts/FilterContext";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import { lazy, Suspense } from "react";

// Eager load: auth pages (needed immediately)
import { LoginPage } from "./pages/LoginPage";

// Lazy load: all dashboard pages (loaded on demand)
const DashboardPage = lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const FilesPage = lazy(() => import("./pages/FilesPage").then(m => ({ default: m.FilesPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const ServiceLinesPage = lazy(() => import("./pages/ServiceLinesPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const PortalChoicePage = lazy(() => import("./pages/PortalChoicePage"));
const AnalystPortalPage = lazy(() => import("./pages/AnalystPortalPage"));
const ContractsPage = lazy(() => import("./pages/ContractsPage"));
const TreasuryPage = lazy(() => import("./pages/TreasuryPage"));
const AdvisorPage = lazy(() => import("./pages/AdvisorPage"));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const ROUTE_PERM_MAP = {
  '/dashboard': 'dashboard',
  '/files': 'files',
  '/settings': 'settings',
  '/service-lines': 'analytics',
  '/clients': 'analytics',
  '/contracts': 'analytics',
  '/treasury': 'analytics',
  '/advisor': 'analytics',
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingFallback />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Admin sees everything
  if (user?.role === 'admin') return children;

  // Check permissions for non-admin users
  const perms = user?.permissions || [];
  const requiredPerm = ROUTE_PERM_MAP[location.pathname];
  if (requiredPerm && !perms.includes(requiredPerm)) {
    // Redirect to first allowed page
    const firstAllowed = Object.entries(ROUTE_PERM_MAP).find(([, p]) => perms.includes(p));
    return <Navigate to={firstAllowed ? firstAllowed[0] : '/login'} replace />;
  }

  return children;
};

const SmartRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'admin') return <Navigate to="/dashboard" replace />;
  const perms = user?.permissions || [];
  const firstAllowed = Object.entries(ROUTE_PERM_MAP).find(([, p]) => perms.includes(p));
  return <Navigate to={firstAllowed ? firstAllowed[0] : '/dashboard'} replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (isAuthenticated) return <Navigate to="/portal" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/security" element={<Navigate to="/settings?tab=sicurezza" replace />} />
        <Route path="/files" element={<ProtectedRoute><FilesPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/admin" element={<Navigate to="/settings?tab=utenti" replace />} />
        <Route path="/service-lines" element={<ProtectedRoute><ServiceLinesPage /></ProtectedRoute>} />
        <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
        <Route path="/portal" element={<ProtectedRoute><PortalChoicePage /></ProtectedRoute>} />
        <Route path="/analyst" element={<ProtectedRoute><AnalystPortalPage /></ProtectedRoute>} />
        <Route path="/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
        <Route path="/treasury" element={<ProtectedRoute><TreasuryPage /></ProtectedRoute>} />
        <Route path="/advisor" element={<ProtectedRoute><AdvisorPage /></ProtectedRoute>} />

        {/* Smart redirect to first allowed page */}
        <Route path="/" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />
        
        {/* Catch all */}
        <Route path="*" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <FilterProvider>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </FilterProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
